/**
 * Coupon mutations using React Relay.
 *
 * Used by BOTH the system- and organization-scoped coupon flows. Create
 * distinguishes scope via the `systemOldId` / `organizationId` fields on
 * `CreateCouponInput`. The legacy REST helpers in `api/coupons.tsx` are no
 * longer used by the coupon form.
 *
 * Note: Run `npm run relay` after editing GraphQL queries to regenerate
 * type artifacts under __generated__.
 */

import { useCallback } from 'react';
import { useMutation, commitMutation } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';

// ============================================================================
// GraphQL Mutations
// ============================================================================

const CreateCouponMutation = graphql`
  mutation useCouponMutationsCreateMutation($input: CreateCouponInput!) {
    createCoupon(input: $input) {
      result {
        id
        oldId
        name
        code
        active
        discount
        discountType
        duration
        uses
        systemId
        systemOldId
        organizationId
        assignmentOnly
        validDays
        validTimeStart
        validTimeEnd
        validFrom
        validUntil
        created
      }
      errors {
        message
        fields
        code
        shortMessage
        vars
      }
    }
  }
`;

const UpdateCouponMutation = graphql`
  mutation useCouponMutationsUpdateMutation(
    $id: ID!
    $input: UpdateCouponInput
    $applicableToSystem: String
  ) {
    updateCoupon(id: $id, input: $input, applicableToSystem: $applicableToSystem) {
      result {
        id
        oldId
        name
        code
        active
        discount
        discountType
        duration
        uses
        systemId
        systemOldId
        organizationId
        assignmentOnly
        validDays
        validTimeStart
        validTimeEnd
        validFrom
        validUntil
        created
      }
      errors {
        message
        fields
        code
        shortMessage
        vars
      }
    }
  }
`;

const DestroyCouponMutation = graphql`
  mutation useCouponMutationsDestroyMutation($id: ID!, $applicableToSystem: String) {
    destroyCoupon(id: $id, applicableToSystem: $applicableToSystem) {
      result {
        id
      }
      errors {
        message
        fields
        code
        shortMessage
        vars
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface CouponMutationError {
  code?: string | null;
  fields?: readonly string[] | null;
  message?: string | null;
  shortMessage?: string | null;
  vars?: unknown;
}

// Legacy alias retained for backwards compatibility with earlier consumers.
export type MutationError = CouponMutationError;

export interface MutationResult<T> {
  data: T | null;
  errors: CouponMutationError[];
  isSuccess: boolean;
}

export interface CreateCouponInput {
  name: string;
  code: string;
  discountType: string;
  discount: number;
  active: boolean;
  uses?: number | null;
  duration?: number | null;
  systemOldId?: string | null;
  organizationId?: string | null;
  /** Coupon can only be applied via admin assignment. */
  assignmentOnly?: boolean | null;
  /** 7-bit weekday bitmask, bit 0 = Monday ... bit 6 = Sunday. */
  validDays?: number | null;
  /** Minutes since local midnight (0-1439). */
  validTimeStart?: number | null;
  /** Minutes since local midnight (0-1439). */
  validTimeEnd?: number | null;
  /** ISO date string (YYYY-MM-DD). */
  validFrom?: string | null;
  /** ISO date string (YYYY-MM-DD). */
  validUntil?: string | null;
}

export interface UpdateCouponInput {
  name?: string;
  code?: string;
  discountType?: string;
  discount?: number;
  active?: boolean;
  uses?: number | null;
  duration?: number | null;
  /** Coupon can only be applied via admin assignment. */
  assignmentOnly?: boolean | null;
  /** 7-bit weekday bitmask, bit 0 = Monday ... bit 6 = Sunday. */
  validDays?: number | null;
  /** Minutes since local midnight (0-1439). */
  validTimeStart?: number | null;
  /** Minutes since local midnight (0-1439). */
  validTimeEnd?: number | null;
  /** ISO date string (YYYY-MM-DD). */
  validFrom?: string | null;
  /** ISO date string (YYYY-MM-DD). */
  validUntil?: string | null;
}

// ============================================================================
// Hooks
// ============================================================================

export function useCreateCouponMutation() {
  const [commit, isInFlight] = useMutation<any>(CreateCouponMutation);

  const createCoupon = useCallback(
    (input: CreateCouponInput) => {
      return new Promise<MutationResult<any>>((resolve) => {
        commit({
          variables: { input },
          onCompleted: (response: any) => {
            const result = response?.createCoupon;
            const errors = (result?.errors ?? []) as CouponMutationError[];
            resolve({
              data: result?.result ?? null,
              errors,
              isSuccess: errors.length === 0 && result?.result != null,
            });
          },
          onError: (error: Error) => {
            resolve({
              data: null,
              errors: [{ message: error.message }],
              isSuccess: false,
            });
          },
        });
      });
    },
    [commit]
  );

  return { createCoupon, isLoading: isInFlight };
}

export function useUpdateCouponMutation() {
  const [commit, isInFlight] = useMutation<any>(UpdateCouponMutation);

  const updateCoupon = useCallback(
    (couponId: string, input: UpdateCouponInput, applicableToSystem?: string) => {
      return new Promise<MutationResult<any>>((resolve) => {
        commit({
          variables: { id: couponId, input, applicableToSystem },
          onCompleted: (response: any) => {
            const result = response?.updateCoupon;
            const errors = (result?.errors ?? []) as CouponMutationError[];
            resolve({
              data: result?.result ?? null,
              errors,
              isSuccess: errors.length === 0 && result?.result != null,
            });
          },
          onError: (error: Error) => {
            resolve({
              data: null,
              errors: [{ message: error.message }],
              isSuccess: false,
            });
          },
        });
      });
    },
    [commit]
  );

  return { updateCoupon, isLoading: isInFlight };
}

export function useDestroyCouponMutation() {
  const [commit, isInFlight] = useMutation<any>(DestroyCouponMutation);

  const destroyCoupon = useCallback(
    (couponId: string, applicableToSystem?: string) => {
      return new Promise<MutationResult<any>>((resolve) => {
        commit({
          variables: { id: couponId, applicableToSystem },
          onCompleted: (response: any) => {
            const result = response?.destroyCoupon;
            const errors = (result?.errors ?? []) as CouponMutationError[];
            resolve({
              data: result?.result ?? null,
              errors,
              isSuccess: errors.length === 0,
            });
          },
          onError: (error: Error) => {
            resolve({
              data: null,
              errors: [{ message: error.message }],
              isSuccess: false,
            });
          },
        });
      });
    },
    [commit]
  );

  return { destroyCoupon, isLoading: isInFlight };
}

// ============================================================================
// Imperative API (without hooks)
// ============================================================================

export function createCouponMutation(
  input: CreateCouponInput
): Promise<MutationResult<unknown>> {
  return new Promise((resolve) => {
    commitMutation<any>(relayEnvironment, {
      mutation: CreateCouponMutation,
      variables: { input },
      onCompleted: (response: any) => {
        const result = response?.createCoupon;
        const errors = (result?.errors ?? []) as CouponMutationError[];
        resolve({
          data: result?.result ?? null,
          errors,
          isSuccess: errors.length === 0 && result?.result != null,
        });
      },
      onError: (error: Error) => {
        resolve({
          data: null,
          errors: [{ message: error.message }],
          isSuccess: false,
        });
      },
    });
  });
}

export function updateCouponMutation(
  couponId: string,
  input: UpdateCouponInput,
  applicableToSystem?: string
): Promise<MutationResult<unknown>> {
  return new Promise((resolve) => {
    commitMutation<any>(relayEnvironment, {
      mutation: UpdateCouponMutation,
      variables: { id: couponId, input, applicableToSystem },
      onCompleted: (response: any) => {
        const result = response?.updateCoupon;
        const errors = (result?.errors ?? []) as CouponMutationError[];
        resolve({
          data: result?.result ?? null,
          errors,
          isSuccess: errors.length === 0 && result?.result != null,
        });
      },
      onError: (error: Error) => {
        resolve({
          data: null,
          errors: [{ message: error.message }],
          isSuccess: false,
        });
      },
    });
  });
}

export function destroyCouponMutation(
  couponId: string,
  applicableToSystem?: string
): Promise<MutationResult<unknown>> {
  return new Promise((resolve) => {
    commitMutation<any>(relayEnvironment, {
      mutation: DestroyCouponMutation,
      variables: { id: couponId, applicableToSystem },
      onCompleted: (response: any) => {
        const result = response?.destroyCoupon;
        const errors = (result?.errors ?? []) as CouponMutationError[];
        resolve({
          data: result?.result ?? null,
          errors,
          isSuccess: errors.length === 0,
        });
      },
      onError: (error: Error) => {
        resolve({
          data: null,
          errors: [{ message: error.message }],
          isSuccess: false,
        });
      },
    });
  });
}
